import api from './api'
import {
  DecisaoFormularioRequest,
  EtapaFormulario,
  FormulariosProdutoEtapa,
  SchemaFormulario,
  VersaoFormularioProduto,
} from '@/types/formularioAdmin'

/**
 * Cadastro dos formulários dinâmicos por produto (AN-6 §6) — `FormularioProdutoController`,
 * no módulo **oportunidades** (é lá que a exigência é imposta, como o checklist da V27).
 * O backend de oportunidades não embrulha a resposta em ApiResponse.
 *
 * GET é OPERADOR_OR_ABOVE; rascunho e publicação são ADMIN_ONLY. Configuração global por
 * produto — o consultor não tem acesso.
 */
const base = (produtoId: string) => `/api/v1/oportunidades/formularios/produto/${produtoId}`

export const formularioAdminService = {
  /** `{vigente, rascunho, historico[]}` da etapa. */
  doProduto: async (produtoId: string, etapa: EtapaFormulario): Promise<FormulariosProdutoEtapa> => {
    const r = (await api.get<FormulariosProdutoEtapa>(
      `${base(produtoId)}?etapa=${etapa}`
    )) as unknown as FormulariosProdutoEtapa | null
    return {
      vigente: r?.vigente ?? null,
      rascunho: r?.rascunho ?? null,
      historico: r?.historico ?? [],
      pendente: r?.pendente ?? null,
    }
  },

  /** Cria ou substitui o rascunho da etapa; o backend valida a estrutura. */
  salvarRascunho: async (
    produtoId: string,
    etapa: EtapaFormulario,
    schema: SchemaFormulario
  ): Promise<VersaoFormularioProduto> =>
    (await api.put<VersaoFormularioProduto>(`${base(produtoId)}/${etapa}/rascunho`, {
      schema,
    })) as unknown as VersaoFormularioProduto,

  /**
   * Envia o rascunho para APROVAÇÃO como versão N+1 (V64/AN-PP6 §3): 202 com `status: PENDENTE_APROVACAO`. Só vale
   * quando outra pessoa (GERENTE/ADMIN) aprovar — aí a anterior vira SUBSTITUIDA. Reservas já vinculadas continuam
   * na versão delas. Schema vazio = sem formulário daqui em diante. 409 `PROPOSTA_JA_PENDENTE`.
   */
  publicar: async (produtoId: string, etapa: EtapaFormulario): Promise<VersaoFormularioProduto> =>
    (await api.post<VersaoFormularioProduto>(
      `${base(produtoId)}/${etapa}/publicar`
    )) as unknown as VersaoFormularioProduto,

  /**
   * Aprova a proposta INDICADA da etapa (revisão PP6-valr, A1: `…/{etapa}/versoes/{versaoId}/aprovar` + `schemaHash`
   * do que foi revisado). GERENTE/ADMIN que NÃO a propôs; 409 `PROPOSTA_DIVERGENTE` se não é mais a pendente.
   */
  aprovar: async (
    produtoId: string,
    etapa: EtapaFormulario,
    versaoId: string,
    request: DecisaoFormularioRequest
  ): Promise<VersaoFormularioProduto> =>
    (await api.post<VersaoFormularioProduto>(
      `${base(produtoId)}/${etapa}/versoes/${versaoId}/aprovar`,
      request
    )) as unknown as VersaoFormularioProduto,

  /** Rejeita a proposta indicada com motivo (mesmas regras de quem aprova). */
  rejeitar: async (
    produtoId: string,
    etapa: EtapaFormulario,
    versaoId: string,
    request: DecisaoFormularioRequest
  ): Promise<VersaoFormularioProduto> =>
    (await api.post<VersaoFormularioProduto>(
      `${base(produtoId)}/${etapa}/versoes/${versaoId}/rejeitar`,
      request
    )) as unknown as VersaoFormularioProduto,

  /** O PRÓPRIO proponente retira a proposta (revisão PP6-valr, 8b): REJEITADA "Retirada pelo proponente". */
  retirar: async (produtoId: string, etapa: EtapaFormulario, versaoId: string): Promise<VersaoFormularioProduto> =>
    (await api.post<VersaoFormularioProduto>(
      `${base(produtoId)}/${etapa}/versoes/${versaoId}/retirar`
    )) as unknown as VersaoFormularioProduto,

  /** Uma versão qualquer (histórico), com o schema completo. */
  versao: async (produtoId: string, versaoId: string): Promise<VersaoFormularioProduto> =>
    (await api.get<VersaoFormularioProduto>(
      `${base(produtoId)}/versoes/${versaoId}`
    )) as unknown as VersaoFormularioProduto,
}
